<template>
  <div class="resizable-split-container" :style="{ '--resize-position': `${resizePercentage}%` }">
    <div class="mobile-half top">
      <slot name="top"></slot>
      <div class="resizer" @mousedown="startResizing" @touchstart="startResizing">
        <div class="resizer-icon">=</div>
      </div>
    </div>
    <div class="mobile-half bottom">
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
const components = {}
@Component({ components })
export default class ResizableSplitContainer extends Vue {
  isResizing = false
  defaultPosition: number = 50
  resizePercentage: number = this.defaultPosition
  resizeObserver: ResizeObserver | null = null

  async mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.resizePercentage = this.defaultPosition
    })
    this.resizeObserver.observe(this.$el as HTMLElement)
  }

  startResizing(event: Event) {
    event.preventDefault()
    this.isResizing = true
    window.addEventListener('mousemove', this.handleResize)
    window.addEventListener('mouseup', this.stopResizing)
    window.addEventListener('touchmove', this.handleResize)
    window.addEventListener('touchend', this.stopResizing)
  }

  stopResizing() {
    this.isResizing = false
    this.$typedStore.public.size.recalculate()
    window.removeEventListener('mousemove', this.handleResize)
    window.removeEventListener('mouseup', this.stopResizing)
    window.removeEventListener('touchmove', this.handleResize)
    window.removeEventListener('touchend', this.stopResizing)
  }

  getTouchPosition(event: MouseEvent | TouchEvent, axis: 'X' | 'Y'): number {
    return 'touches' in event ? event.touches[0][`client${axis}`] : event[`client${axis}`]
  }

  handleResize(event: MouseEvent | TouchEvent) {
    if (!this.isResizing) return
    const container = this.$el as HTMLElement
    const landscape = container.offsetHeight < container.offsetWidth
    const minSize = landscape ? 380 : 119
    const position = Math.max(minSize, this.getTouchPosition(event, landscape ? 'X' : 'Y'))
    const containerDimension = landscape ? container.offsetWidth : container.offsetHeight
    this.resizePercentage = Math.max(0, Math.min((position / containerDimension) * 100, 100))
  }

  beforeDestroy() {
    this.stopResizing()
  }
}
</script>

<style scoped lang="scss">
.resizable-split-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overscroll-behavior: none;
  .top {
    display: flex;
    position: relative;
    z-index: 2;
    justify-content: space-between;
    .resizer {
      background-color: #28292a;
      color: #ccc;
      display: flex;
      justify-content: center;
      align-items: center;
      .resizer-icon {
        transform: scaleX(2.5);
      }
    }
  }
  .bottom {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
    flex: 1;
  }
  @media (orientation: portrait) {
    flex-direction: column;
    .top {
      flex-direction: column;
      height: var(--resize-position);
      .resizer {
        padding-bottom: 4px;
        width: 100%;
        height: 15px;
      }
    }
    .bottom {
      height: calc(100% - var(--resize-position));
    }
    .mobile-half {
      width: 100%;
    }
  }
  @media (orientation: landscape) {
    flex-direction: row;

    .top {
      flex-direction: row;
      width: var(--resize-position);
      .resizer {
        height: 100%;
        width: 15px;
        padding-left: 4px;
        .resizer-icon {
          transform: rotateZ(90deg) scaleX(2.5);
        }
      }
    }
    .bottom {
      width: calc(100% - var(--resize-position));
    }
    .mobile-half {
      height: 100%;
    }
  }
}
</style>
